import React, { ReactNode } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { TextInput } from '../../../common/TextInput'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { FormControl } from '../../../common/FormControl'
import { societyDocumentFolderCreateSchema } from '../../../../forms/schemas/society_document_folder_create'
import { Image } from '../../../common/Image'
import {
  DropZone,
  DropZoneFile,
  DropZoneVariant,
} from '../../../common/DropZone'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { TextArea } from '../../../common/TextArea'
import { ImagePreviewModal } from '../../../common/ImagePreviewModal'
import { createMedia } from '../../../../api/media'
import { useModal } from '../../../../hooks/useModal'

interface CreateUpdateFolderFormProps {
  control: Control<InferType<typeof societyDocumentFolderCreateSchema>>
  setValue: UseFormSetValue<InferType<typeof societyDocumentFolderCreateSchema>>
  errors: FieldErrors<InferType<typeof societyDocumentFolderCreateSchema>>
}

export const CreateUpdateFolderForm = ({
  control,
  setValue,
  errors,
}: CreateUpdateFolderFormProps): ReactNode => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()
  const { setToastNotification } = useToastNotifications()

  const {
    show: showImagePreviewModal,
    close: closeImagePreviewModal,
    open: openImagePreviewModal,
  } = useModal()

  const { mutate: mutateCreateThumbnail, isPending: isUploadingPicture } =
    useMutation({
      mutationFn: async ({ base64 }: { base64: string }) => {
        const media = (await createMedia('image', base64)).data.data
        if (!media) {
          throw new Error('no media returned by createMedia')
        }
        return media
      },
      onSuccess: (data) => {
        setValue('thumbnail', data.url, {
          shouldValidate: true,
          shouldDirty: true,
        })
      },
      onError: () => {
        setToastNotification(
          ToastType.DANGER,
          translate('flashMessage.somethingWentWrongError')
        )
      },
    })
  const uploadImage = async (file: DropZoneFile): Promise<void> => {
    mutateCreateThumbnail({ base64: file.base64 })
  }

  return (
    <>
      <Controller
        control={control}
        render={({ field: { name, value } }) => (
          <FormControl
            label={translate('createUpdateFolderForm.labels.thumbnail')}
            error={errors.thumbnail && getErrorMessage(errors.thumbnail)}
            name={name}
          >
            {value ? (
              <>
                <div className="flex justify-center h-40">
                  <Image
                    url={value}
                    onDeleteClick={() =>
                      setValue('thumbnail', '', {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                    onClick={openImagePreviewModal}
                    objectFit="object-contain"
                  />
                </div>
                <ImagePreviewModal
                  showPreviewModal={showImagePreviewModal}
                  url={value}
                  onClose={closeImagePreviewModal}
                  text={translate('createUpdateFolderForm.labels.thumbnail')}
                />
              </>
            ) : (
              <div>
                <DropZone
                  loading={isUploadingPicture}
                  onUpload={(status, file) => {
                    if (status === 'accepted') {
                      uploadImage(file)
                    } else {
                      setToastNotification(
                        ToastType.DANGER,
                        translate('flashMessage.somethingWentWrongError')
                      )
                    }
                  }}
                  variant={DropZoneVariant.MEDIA}
                />
              </div>
            )}
          </FormControl>
        )}
        name="thumbnail"
      />
      <Controller
        control={control}
        name="name"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate('createUpdateResourceModal.labels.title')}
            error={errors.name && getErrorMessage(errors.name)}
            name={name}
          >
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.name}
            />
          </FormControl>
        )}
      />

      <Controller
        control={control}
        name="description"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate('createUpdateResourceModal.labels.description')}
            name={name}
            error={errors.description && getErrorMessage(errors.description)}
          >
            <TextArea
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.description}
              rows={15}
            />
          </FormControl>
        )}
      />
    </>
  )
}
